import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { FaUpload } from 'react-icons/fa';

// Impor semua CSS yang dibutuhkan
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerCustom.css';
import './ImageUploader.css';

const KendaraanForm = ({ existingData, onSave, onCancel }) => {
  // State untuk semua field form
  const [nomorKendaraan, setNomorKendaraan] = useState('');
  const [jenis, setJenis] = useState('');
  const [spesifikasi, setSpesifikasi] = useState('');
  const [tahun, setTahun] = useState(null);
  const [pajak, setPajak] = useState('');
  const [tanggalPajak, setTanggalPajak] = useState('');

  // State untuk manajemen gambar (diubah menjadi array)
  const [newImageFiles, setNewImageFiles] = useState([]); // File object baru
  const [previews, setPreviews] = useState([]); // URL preview untuk file baru
  const [existingImages, setExistingImages] = useState([]); // Nama file gambar lama
  const [activeIndex, setActiveIndex] = useState(0); // Index gambar yang aktif di slider

  const fileInputRef = useRef(null); // Referensi untuk input file tersembunyi

  useEffect(() => {
    const data = existingData || {};
    setNomorKendaraan(data.nomorKendaraan || '');
    setJenis(data.jenis || '');
    setSpesifikasi(data.spesifikasi || '');
    setTahun(data.tahun ? new Date(data.tahun) : null);
    setPajak(data.pajak || '');
    setTanggalPajak(data.tanggalPajak ? new Date(data.tanggalPajak).toISOString().split('T')[0] : '');
    
    // Reset state gambar setiap kali form dibuka
    setNewImageFiles([]);
    setPreviews([]);
    setExistingImages(data.gambar || []);
    setActiveIndex(0);
  }, [existingData]);

  // Handler saat file dipilih atau di-drop (mendukung multi-file)
  const handleFileChange = (files) => {
    const newFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (newFiles.length === 0) return;
    
    const newFileObjects = [...newImageFiles, ...newFiles];
    const newPreviews = [...previews, ...newFiles.map(file => URL.createObjectURL(file))];
    
    setNewImageFiles(newFileObjects);
    setPreviews(newPreviews);
  };
  
  // Handler untuk menghapus gambar dari slider
  const handleRemoveImage = (e, indexToRemove) => {
    e.stopPropagation();
    
    if (indexToRemove < existingImages.length) {
      // Menghapus gambar dari daftar 'existingImages'
      setExistingImages(existingImages.filter((_, i) => i !== indexToRemove));
    } else {
      // Menghapus gambar dari daftar 'previews' dan 'newImageFiles'
      const previewIndex = indexToRemove - existingImages.length;
      URL.revokeObjectURL(previews[previewIndex]); // Mencegah memory leak
      setPreviews(previews.filter((_, i) => i !== previewIndex));
      setNewImageFiles(newImageFiles.filter((_, i) => i !== previewIndex));
    }
    // Reset active index jika gambar yang dihapus adalah yang terakhir
    if (activeIndex >= displayImages.length - 1) {
        setActiveIndex(0);
    }
  };

  // Handler saat form di-submit
  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('nomorKendaraan', nomorKendaraan);
    formData.append('jenis', jenis);
    formData.append('spesifikasi', spesifikasi);
    if (tahun) formData.append('tahun', tahun.toISOString());
    formData.append('pajak', pajak);
    formData.append('tanggalPajak', tanggalPajak);

    // Kirim gambar lama yang masih dipertahankan
    existingImages.forEach(img => formData.append('existingImages', img));
    // Kirim file gambar baru yang akan di-upload
    newImageFiles.forEach(file => formData.append('gambar', file));
    
    onSave(formData);
  };

  const displayImages = [...existingImages.map(img => `${process.env.REACT_APP_API_URL}/uploads/${img}`), ...previews];

  return (
    <form onSubmit={handleSubmit} className="form-card">
      <div className="form-grid-container">
        
        {/* --- KOLOM KIRI --- */}
        <div className="form-column">
          <div className="form-group">
            <label>Nomor Kendaraan</label>
            <input type="text" value={nomorKendaraan} onChange={(e) => setNomorKendaraan(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Jenis</label>
            <input type="text" value={jenis} onChange={(e) => setJenis(e.target.value)} placeholder="Contoh: Innova Zenix Q HV" required />
          </div>
          <div className="form-group">
            <label>Spesifikasi</label>
            <input type="text" value={spesifikasi} onChange={(e) => setSpesifikasi(e.target.value)} placeholder="Contoh: Mobil Hitam" required />
          </div>
          <div className="form-group">
            <label>Bulan & Tahun Kendaraan</label>
            <DatePicker selected={tahun} onChange={(date) => setTahun(date)} dateFormat="MM / yyyy" showMonthYearPicker placeholderText="Pilih Bulan / Tahun" className="custom-datepicker-input" required />
          </div>
        </div>

        {/* --- KOLOM KANAN --- */}
        <div className="form-column">
          <div className="form-group">
            <label>Pajak</label>
            <input type="number" value={pajak} onChange={(e) => setPajak(e.target.value)} placeholder="Contoh: 2500000" required />
          </div>
          <div className="form-group">
            <label>Tanggal Pajak</label>
            <input type="date" value={tanggalPajak} onChange={(e) => setTanggalPajak(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Image</label>
            <div
              className={`drop-zone ${displayImages.length > 0 ? 'drop-zone--has-image' : ''}`}
              style={displayImages.length > 0 ? { backgroundImage: `url(${displayImages[activeIndex]})` } : {}}
              onClick={() => fileInputRef.current.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFileChange(e.dataTransfer.files); }}
            >
              <input type="file" ref={fileInputRef} onChange={(e) => handleFileChange(e.target.files)} className="drop-zone__input" accept="image/*" multiple />
              
              {displayImages.length > 0 ? (
                <>
                  <button type="button" className="drop-zone__remove-btn" onClick={(e) => handleRemoveImage(e, activeIndex)}>×</button>
                  <div className="drop-zone__overlay"><span>Ganti / Tambah Gambar</span></div>
                </>
              ) : (
                <div className="drop-zone__prompt">
                  <div className="drop-zone__prompt-icon"><FaUpload /></div>
                  <p>Drag & Drop atau klik untuk menambah gambar</p>
                </div>
              )}
            </div>
            {displayImages.length > 1 && (
              <div className="dots-container">
                {displayImages.map((_, index) => (
                  <div key={index} className={`dot ${activeIndex === index ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setActiveIndex(index); }} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #f0f0f0' }}>
        <button type="button" onClick={onCancel} className="btn-secondary">Batal</button>
        <button type="submit" className="btn-primary">Simpan</button>
      </div>
    </form>
  );
};
export default KendaraanForm;